import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { subscribe } from '@/lib/api/newsletter';
import { useSettings } from '@/hooks/useSettings';

export default function Footer() {
  const { t } = useTranslation();
  const settings = useSettings();

  const seoKeywords = [
    ...Array.from({ length: 25 }, (_, i) => t(`pub.fkw_${i + 1}`)),
    'Esteva Clinic',
  ];
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Live contact details (fall back to sensible defaults before hydration)
  const phone = settings?.phone || '+41 76 559 39 93';
  const phoneHref = `tel:${(settings?.phone || '+41765593993').replace(/[^+\d]/g, '')}`;
  const emailAddr = settings?.email || 'info@esteva.ch';
  const instagramUrl = settings?.instagram || 'https://instagram.com/estevaclinic';
  const tiktokUrl = settings?.tiktok || '';
  const openingHours = (settings?.opening_hours || t('pub.opening_hours_default'))
    .split('|').map((s) => s.trim()).filter(Boolean);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const language = localStorage.getItem('i18nextLng') || 'de';

      // Subscribe (unique email handled at the DB level under RLS)
      const result = await subscribe(email, language);
      if (result === 'exists') {
        setStatus('error');
        setErrorMsg(t('home.newsletter_exists'));
        return;
      }

      // Send welcome email via Edge Function (best-effort)
      try {
        const edgeUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-welcome-email`;
        await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            language: language.split('-')[0],
          }),
        });
      } catch {
        // Edge function failure should not block success UI
      }

      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
      setErrorMsg(t('home.newsletter_error'));
    }
  };

  return (
    <footer className="bg-primary-900 text-background-50">
      <div className="px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-between gap-8 mb-12">
            <div className="max-w-xs">
              <span className="font-heading text-xl md:text-2xl tracking-[0.15em] text-background-50">
                ESTEVA CLINIC
              </span>
              <p className="mt-2 text-sm text-background-200/70 font-body leading-relaxed">
                {settings?.footer_tagline || t('footer.tagline')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-6 lg:gap-10 flex-wrap">
              <a
                href={phoneHref}
                className="flex items-center gap-3 group cursor-pointer no-underline"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-800 group-hover:bg-primary-700 transition-colors flex-shrink-0 border border-primary-600/40">
                  <i className="ri-phone-line text-sm text-primary-300"></i>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-background-400 font-label">{t('imprint.phone_label')}</p>
                  <p className="text-sm text-background-50 font-body group-hover:text-background-200 transition-colors">{phone}</p>
                </div>
              </a>
              <a
                href={`mailto:${emailAddr}`}
                className="flex items-center gap-3 group cursor-pointer no-underline"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-800 group-hover:bg-primary-700 transition-colors flex-shrink-0 border border-primary-600/40">
                  <i className="ri-mail-line text-sm text-primary-300"></i>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-background-400 font-label">{t('imprint.email_label')}</p>
                  <p className="text-sm text-background-50 font-body group-hover:text-background-200 transition-colors">{emailAddr}</p>
                </div>
              </a>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-800 flex-shrink-0 border border-primary-600/40">
                  <i className="ri-time-line text-sm text-primary-300"></i>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-background-400 font-label mb-1">{t('contact.opening_hours')}</p>
                  <div className="space-y-0.5">
                    {openingHours.map((line) => (
                      <p key={line} className="text-xs text-background-50 font-body">{line}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-14">
            <div>
              <h4 className="text-xs tracking-[0.15em] uppercase text-background-50/80 font-label font-semibold mb-4">
                {t('footer.services')}
              </h4>
              <ul className="space-y-2.5">
                <li><a href="/services" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.dental')}</a></li>
                <li><a href="/services" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.surgery')}</a></li>
                <li><a href="/contact" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('nav.booking')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs tracking-[0.15em] uppercase text-background-50/80 font-label font-semibold mb-4">
                {t('footer.about')}
              </h4>
              <ul className="space-y-2.5">
                <li><a href="/team" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.team')}</a></li>
                <li><a href="/about" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.philosophy')}</a></li>
                <li><a href="/contact" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.contact')}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs tracking-[0.15em] uppercase text-background-50/80 font-label font-semibold mb-4">
                {t('footer.legal')}
              </h4>
              <ul className="space-y-2.5">
                <li><a href="/contact" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.imprint')}</a></li>
                <li><a href="/privacy" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.privacy')}</a></li>
                <li><a href="/agb" className="text-sm text-background-200/70 hover:text-background-50 transition-colors font-body cursor-pointer">{t('footer.terms')}</a></li>
              </ul>
            </div>

            <div>
              <p className="text-2xl md:text-3xl font-heading text-background-50/90 leading-tight">
                {t('footer.copyright').split('.')[0]}
              </p>
              <p className="text-xs text-background-300/60 font-body italic mt-1">
                All rights reserved
              </p>
              <div className="flex items-center gap-3 md:gap-4 mt-4 flex-wrap">
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-gradient-to-br from-accent-500 to-secondary-500 text-background-50 hover:scale-110 transition-all duration-300 cursor-pointer" aria-label="Instagram">
                  <i className="ri-instagram-line text-base md:text-lg"></i>
                </a>
                {tiktokUrl && (
                  <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-primary-800 text-background-300 hover:text-background-50 hover:bg-primary-700 transition-all duration-300 cursor-pointer" aria-label="TikTok">
                    <i className="ri-tiktok-fill text-base md:text-lg"></i>
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-primary-700 pt-8 md:pt-12">
            <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center lg:items-start">
              <div className="flex-1">
                <p className="font-heading text-xl md:text-3xl text-background-50/85 leading-snug">
                  {t('footer.newsletter_title')}
                </p>
                <p className="text-sm text-background-200/70 font-body leading-relaxed mt-3 max-w-md">
                  {t('footer.newsletter_subtitle')}
                </p>
              </div>

              <div className="w-full lg:w-96">
                <form
                  onSubmit={handleNewsletterSubmit}
                  className="flex flex-col sm:flex-row gap-2"
                >
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === 'loading'}
                    placeholder={t('footer.newsletter_placeholder')}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-primary-800 border border-primary-600 text-sm text-background-50 placeholder:text-background-400 font-body focus:outline-none focus:border-primary-400 transition-all disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-xs tracking-[0.1em] uppercase font-label font-semibold hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        {t('footer.newsletter_loading')}
                      </>
                    ) : (
                      <>
                        {t('footer.newsletter_button')}
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </button>
                </form>
                {status === 'success' && (
                  <p className="mt-2 text-xs text-accent-400 font-body">
                    {t('footer.newsletter_success')}
                  </p>
                )}
                {status === 'error' && (
                  <p className="mt-2 text-xs text-red-400 font-body">
                    {errorMsg}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* ─── SEO Block ─── */}
          <div className="border-t border-primary-700 pt-6 md:pt-8 mt-6 md:mt-8">
            {/* Block 1: SEO Pills */}
            <div className="flex flex-wrap gap-2 mb-4">
              {seoKeywords.map((keyword) => (
                <span
                  key={keyword}
                  className="inline-flex items-center px-2.5 py-1 rounded-full border border-primary-600/40 text-[11px] text-background-300/70 font-body whitespace-nowrap"
                >
                  {keyword}
                </span>
              ))}
            </div>
            {/* Block 2: SEO Text */}
            <p className="text-[11px] text-background-300/50 font-body leading-relaxed max-w-full">
              {t('footer.seo_text')}
            </p>
          </div>
        </div>
      </div>

      {/* ─── Copyright Bar ─── */}
      <div className="border-t border-primary-700">
        <div className="px-6 md:px-10 py-5">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
            <div>
              <p className="text-xs text-background-300/60 font-body">
                {settings?.footer_copyright || t('footer.copyright')}
              </p>
              <p className="text-xs text-background-300/60 font-body mt-1">
                {t('home.developed_by')} AI VISION GmbH
              </p>
            </div>
            <div className="flex items-center gap-5">
              <a href="/privacy" className="text-xs text-background-300/60 hover:text-background-200 transition-colors font-body cursor-pointer">{t('footer.privacy')}</a>
              <a href="/agb" className="text-xs text-background-300/60 hover:text-background-200 transition-colors font-body cursor-pointer">{t('footer.terms')}</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}